<script setup lang="ts">
import { computed } from 'vue';

import { PriceGuaranteeBanner } from '@apps/main/new-desktop/modules/priceGuarantee';
import {
  useBrowserStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';
import { i18n } from '@apps/main/modules/localization/localizationSetup';

import { AppButton, AppSvgIcon } from '@libs/ui';

import { useGlobalCurrencyStore } from '../../../../core/stores/globalCurrencyStore';
import { useTripOverviewModalsStore, useTripSummaryStore } from '../../stores';

interface TripPriceSummaryFooterProps {
  totalPrice: number;
  totalAddonsPrice: number;
  paxes: string;
  travelDates: string;
  shouldShowShareButton: boolean;
}

const props = defineProps<TripPriceSummaryFooterProps>();

interface TripPriceSummaryFooterEmits {
  (eventName: 'total-price-clicked'): void;
}

const emits = defineEmits<TripPriceSummaryFooterEmits>();

const tripOverviewModalsStore = useTripOverviewModalsStore();
const tripSummaryStore = useTripSummaryStore();
const globalCurrencyStore = useGlobalCurrencyStore();
const browserStore = useBrowserStore();
const statsigEventsStore = useStatsigEventsStore();

const { t } = i18n.global;

const bookButtonText = computed(() =>
  browserStore.isSmallerThan1200px
    ? t('planner.book_trip')
    : t('planner.booking_proceed')
);

function onTotalPriceClicked(): void {
  if (browserStore.isLargerThan1199px) {
    statsigEventsStore.logTripOverviewTripSummaryPanelOpened();
    emits('total-price-clicked');
  }
}
</script>

<template>
  <div class="trip-price-summary-footer">
    <div class="trip-info">
      <div class="pax">
        <AppSvgIcon
          name="peopleOutlined"
          color="color-neutral-70"
          size="18"
        />
        <span class="value">
          {{ props.paxes }}
        </span>
      </div>
      <div class="date">
        <AppSvgIcon
          name="calendarOutlined"
          color="color-neutral-70"
          size="18"
        />
        <span class="value">
          {{ props.travelDates }}
        </span>
      </div>
    </div>
    <div class="banners">
      <PriceGuaranteeBanner
        @learn-more="tripOverviewModalsStore.openPriceGuaranteeModal"
      />
    </div>
    <div class="total-price">
      <div class="text">{{ $t('planner.total_price') }}</div>
      <div class="amount" @click="onTotalPriceClicked">
        {{
          globalCurrencyStore.formatMoney(props.totalPrice + totalAddonsPrice)
        }}
      </div>
    </div>

    <div class="trip-summary-actions">
      <AppButton
        type="important"
        size="large"
        :state="tripSummaryStore.bookingButtonState"
        @click="tripSummaryStore.onBookClicked"
      >
        {{ bookButtonText }}
      </AppButton>

      <AppButton
        v-if="props.shouldShowShareButton"
        type="secondary"
        state="enabled"
        size="large"
        :wide="true"
        @click="tripSummaryStore.openShareWidget"
      >
        {{ $t('planner.share_or_save_trip') }}
      </AppButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
.trip-price-summary-footer {
  display: flex;
  gap: var(--spacing-24);
  flex-direction: column;
  align-self: center;
  width: 100%;

  .trip-info {
    display: flex;
    gap: var(--spacing-24);
    flex-direction: row;
    align-self: center;
    justify-content: center;
    flex-wrap: wrap;
    @include for-less-or-equal-than-1200-px {
      flex-direction: column;
      padding: var(--spacing-24) 0;
    }

    .pax,
    .date {
      display: flex;
      align-items: center;

      gap: var(--spacing-8);
      white-space: nowrap;
      @include for-less-or-equal-than-1200-px {
        justify-content: center;
      }
      .label {
        @include font-label-l-bold;
        @include for-more-or-equal-than-1200-px {
          @include font-label-s-bold;
        }
      }

      .value {
        color: var(--color-neutral-70);
        @include font-label-l-light;
        line-height: var(--line-heights-14);
      }
    }
  }

  .total-price {
    color: var(--color-neutral-70);
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    @include for-less-or-equal-than-1200-px {
      margin: var(--spacing-24) 0;
    }
    .text {
      @include font-label-l-light;
    }

    .amount {
      @include font-label-xxl-bold;

      @include for-more-or-equal-than-1200-px {
        @include on-hover {
          text-decoration: underline;
          cursor: pointer;
        }
      }
    }
  }

  .trip-summary-actions {
    display: flex;
    flex-flow: column;
    gap: var(--spacing-12);
    .share-and-save-buttons-wrapper {
      margin-top: var(--spacing-4);
      display: flex;
      flex-wrap: wrap;
      justify-content: space-evenly;
    }
  }

  @include for-less-or-equal-than-1200-px {
    gap: 0;
  }
}
</style>
